import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import * as React from 'react';

// material-ui
import {
  Button,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

// project imports
import SubCard from '../cards/SubCard/SubCard';
import ContextActionGuard from 'utils/guards/ContextActionGuard';
import { EnhancedTableHead } from './BasicTable';
import Chip from 'components/assets/Chip';

// types
import {
  AppModule,
  DateFormat,
  TableRowCellType,
  MAXIMAL_VALID_TO_DATE,
  TableActionType
} from 'utils/constants';
import { KeyedObject, ValidityType } from 'models';
import { HeadCell, TableAction } from 'models/table';
import { Theme } from '@mui/material/styles';
import TableClickableCell from 'components/tables/TableClickableCell';
import { useAppSelector } from 'hooks/useAppStore';
import { selectAjaxLoader } from 'features/app/appSelectors';
import { StyledActionButton } from 'views/modules/Dispatcher/dispatcherUtils/dispatcherUtils';
import NotFoundRow from 'components/tables/NotFoundRow';

export interface SortingProperties {
  handleRequestSort?: (e: React.SyntheticEvent, p: string) => void;
  sortingHeader?: HeadCell[];
}

export interface DenseTableProps<T> {
  module: AppModule;
  i18nTableRoot: string;
  title?: string | ReactElement;
  actions?: TableAction[];
  data: T[];
  sorting?: SortingProperties;
}

// constants
const getIsValidFor = (item: string): boolean => item === 'validFor';

// ==============================|| TABLE - DENSE ||============================== //

export default function DenseTable({
  module,
  i18nTableRoot,
  title,
  actions,
  data,
  sorting
}: Readonly<DenseTableProps<any>>) {
  const { t, i18n } = useTranslation();
  const operationInProgress = useAppSelector(selectAjaxLoader);

  const [selected, setSelected] = React.useState<string[]>([]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (selected.length > 0) {
        setSelected([]);
      } else {
        const newSelectedId: string[] = data.map((n) => n.id?.toString());
        setSelected(newSelectedId);
      }
      return;
    }
    setSelected([]);
  };

  const getTableRowContent = (
    row: KeyedObject,
    detailAction: TableAction
  ): ReactElement => {
    const rowProps: SxProps<Theme> | undefined = detailAction
      ? {
          cursor: 'pointer',
          pl: 3
        }
      : { pl: 3 };
    return (
      <>
        {Object.entries(row).map(([key, value], index) => {
          const notActionsCell = !(key === TableRowCellType.ACTIONS);
          return notActionsCell &&
            detailAction &&
            detailAction.contextAction &&
            detailAction.onClick ? (
            <TableClickableCell
              contextAction={detailAction.contextAction}
              onClick={detailAction.onClick}
              key={key}
              sx={{ pl: 3 }}
              component='td'
              scope='row'
              align={index === 0 ? 'left' : 'right'}
            >
              {getValue(key, value)}
            </TableClickableCell>
          ) : (
            <TableCell
              key={key}
              sx={rowProps}
              component='td'
              scope='row'
              align={index === 0 ? 'left' : 'right'}
              onClick={() => {
                if (notActionsCell && detailAction && detailAction.onClick) {
                  detailAction.onClick();
                }
              }}
            >
              {getValue(key, value)}
            </TableCell>
          );
        })}
      </>
    );
  };

  const getRow = (row: KeyedObject, index: number): ReactElement => {
    const detailAction: TableAction = row[TableRowCellType.ACTIONS]?.find(
      (action: TableAction) => action.type === TableActionType.DETAIL
    );

    return (
      <TableRow hover key={index}>
        {getTableRowContent(row, detailAction)}
      </TableRow>
    );
  };
  const getValue = (key: string, value: any): ReactElement => {
    const isValidFor: boolean = getIsValidFor(key);

    const createValidFor = () => {
      const { validFromDate, validToDate } = value as ValidityType;

      return (
        <>
          {moment(validFromDate).format(DateFormat.DATE_FORMAT)} -{' '}
          {moment(validToDate).isSame(MAXIMAL_VALID_TO_DATE) ? (
            <Chip
              label={t('common.table.current') || ''}
              chipColor='primary'
              size='small'
            />
          ) : (
            moment(validToDate).format(DateFormat.DATE_FORMAT)
          )}
        </>
      );
    };

    switch (typeof value) {
      case 'string': {
        const codebookPath = `module.${module}.codebooks.${value}`;
        return (
          <>{i18n.exists(codebookPath) ? t(`${codebookPath}.title`) : value}</>
        );
      }
      case 'number':
        return <>{value}</>;
      case 'object':
        if (key === TableRowCellType.ACTIONS) {
          return (
            <Stack
              direction='row'
              alignItems='flex-end'
              justifyContent={'flex-end'}
              spacing={1}
            >
              {value.map((action: TableAction, index: number) => {
                if (action.contextAction) {
                  return (
                    <ContextActionGuard
                      key={index}
                      module={module}
                      actionCode={action.contextAction?.actionCode}
                      resourceEntity={action.contextAction?.resourceEntity}
                      entityIdentificator={
                        action.contextAction?.entityIdentificator
                      }
                    >
                      <StyledActionButton
                        key={index}
                        onClick={action.onClick}
                        title={action.title}
                        variant={action.variant || 'outlined'}
                        color={action.color}
                      >
                        {action.icon}
                      </StyledActionButton>
                    </ContextActionGuard>
                  );
                }
                return (
                  <StyledActionButton
                    key={index}
                    onClick={action.onClick}
                    title={action.title}
                    variant={action.variant || 'outlined'}
                    color={action.color}
                  >
                    {action.icon}
                  </StyledActionButton>
                );
              })}
            </Stack>
          );
        }
        if (isValidFor) return createValidFor();
        return <>{value}</>;
      default:
        return <>{t(`common.table.unknownType`)}</>;
    }
  };

  const MainCardTitle = title
    ? typeof title === 'string'
      ? t(`${i18nTableRoot}.${title}`)
      : title
    : undefined;

  const handleSortRequest = (e: React.SyntheticEvent, p: string): void => {
    if (sorting?.handleRequestSort) {
      sorting.handleRequestSort(e, p);
    }
  };

  return (
    <SubCard
      content={false}
      title={MainCardTitle}
      secondary={
        <Stack direction='row' spacing={2} alignItems='center'>
          {actions?.map((action, index) => (
            <Button
              key={index}
              variant='contained'
              size='small'
              onClick={action.onClick}
              disabled={operationInProgress}
            >
              {t(action.title || '')}
            </Button>
          ))}
        </Stack>
      }
    >
      <TableContainer>
        <Table sx={{ width: '100%' }} size='small' aria-label='a dense table'>
          {sorting?.sortingHeader && (
            <EnhancedTableHead
              numSelected={selected.length}
              order={'asc'}
              orderBy={'orderBy'}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleSortRequest}
              rowCount={data.length}
              headCells={sorting.sortingHeader || []}
              batchProcessing={false}
            />
          )}

          {!data.length && (
            <TableHead>
              <NotFoundRow colSpan={12} />
            </TableHead>
          )}

          {!sorting?.sortingHeader && !!data.length && (
            <TableHead>
              <TableRow>
                {Object.keys(data[0]).map((item, index) => (
                  <TableCell
                    key={index}
                    component='th'
                    align={index === 0 ? 'left' : 'right'}
                  >
                    {t(
                      `${
                        getIsValidFor(item) ? 'common.table' : i18nTableRoot
                      }.${item}`
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {data.map((row: KeyedObject, index) => {
              return getRow(row, index);
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </SubCard>
  );
}
